import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import clsx from "clsx";

// ── Rarity config ──────────────────────────────────────────────────────────────
const RARITY = {
  common:    { color: "#9ca3af", glow: "rgba(156,163,175,0.35)", bg: "rgba(156,163,175,0.06)", border: "rgba(156,163,175,0.28)", label: "Gewöhnlich" },
  rare:      { color: "#3b82f6", glow: "rgba(59,130,246,0.45)",  bg: "rgba(59,130,246,0.07)",  border: "rgba(59,130,246,0.5)",   label: "Selten"     },
  epic:      { color: "#8b5cf6", glow: "rgba(139,92,246,0.5)",   bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.6)",   label: "Episch"     },
  legendary: { color: "#fbbf24", glow: "rgba(251,191,36,0.55)",  bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.72)",  label: "Legendär"   },
};

const CONDITION_LABELS = {
  xp:         (v) => `${v.toLocaleString()} XP erreichen`,
  streak:     (v) => `${v} Tage Streak`,
  quiz_count: (v) => `${v} Quizze abschließen`,
  coins:      (v) => `${v} Coins besitzen`,
  task_count: (v) => `${v} Aufgaben erledigen`,
  manual:     ()  => "Wird manuell vergeben",
};

function getConditionHint(condition, conditionValue) {
  const fn = CONDITION_LABELS[condition];
  return fn ? fn(conditionValue) : condition;
}

// ── Badge card (earned) ────────────────────────────────────────────────────────
function EarnedBadge({ sb, onToggleShowcase }) {
  const meta = RARITY[sb.badge.rarity] || RARITY.common;
  const isLegendary = sb.badge.rarity === "legendary";

  return (
    <div
      className={clsx(
        "relative flex flex-col items-center p-4 rounded-2xl border-2 text-center overflow-hidden group",
        "transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] cursor-default",
      )}
      style={{
        borderColor: meta.border,
        background:  meta.bg,
        boxShadow:   `0 4px 20px ${meta.glow.replace(/[\d.]+\)$/, "0.2)")}, inset 0 0 0 1px rgba(255,255,255,0.03)`,
        animation:   isLegendary ? "legendary-pulse 2.5s ease-in-out infinite" : undefined,
      }}
    >
      {/* Shimmer top line */}
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg,transparent,${meta.color}55,transparent)` }}/>

      {/* Rarity chip */}
      <div
        className="absolute top-2 right-2 text-[8px] font-black px-1.5 py-0.5 rounded-md tracking-wide"
        style={{ color: meta.color, background: `${meta.color}18`, border: `1px solid ${meta.color}30` }}
      >
        {meta.label.toUpperCase()}
      </div>

      {/* Showcase pin indicator */}
      {sb.isShowcase && (
        <div className="absolute top-2 left-2 text-[10px]" title="Ausgestellt">📌</div>
      )}

      {/* Icon */}
      <div
        className="text-4xl mb-2 transition-transform duration-300 group-hover:scale-110"
        style={{ filter: `drop-shadow(0 0 14px ${meta.glow})` }}
      >
        {sb.badge.icon}
      </div>

      <p className="font-black text-xs leading-tight mb-1">{sb.badge.name}</p>
      <p className="text-[10px] text-gray-500 leading-relaxed mb-2">{sb.badge.description}</p>
      <p className="text-[9px] text-gray-600">
        {new Date(sb.unlockedAt).toLocaleDateString("de-DE")}
      </p>

      {/* Showcase toggle */}
      <button
        onClick={() => onToggleShowcase(sb.id)}
        className={clsx(
          "mt-2 text-[9px] font-bold px-2 py-1 rounded-lg transition-all duration-200",
          sb.isShowcase
            ? "bg-violet-500/20 text-violet-300 border border-violet-500/40"
            : "bg-white/5 text-gray-500 border border-white/10 hover:text-gray-300"
        )}
      >
        {sb.isShowcase ? "📌 Ausgestellt" : "Ausstellen"}
      </button>
    </div>
  );
}

// ── Locked badge card ─────────────────────────────────────────────────────────
function LockedBadge({ badge }) {
  const meta = RARITY[badge.rarity] || RARITY.common;
  return (
    <div
      className="relative flex flex-col items-center p-4 rounded-2xl border text-center overflow-hidden opacity-40 hover:opacity-60 transition-all duration-200 cursor-default"
      style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(13,13,43,0.5)" }}
    >
      <div className="absolute top-2 right-2 text-[8px] font-black px-1.5 py-0.5 rounded-md tracking-wide"
        style={{ color: meta.color, background: `${meta.color}18`, border: `1px solid ${meta.color}25` }}>
        {meta.label.toUpperCase()}
      </div>

      <div className="text-4xl mb-2 grayscale opacity-60">🔒</div>
      <p className="font-black text-xs leading-tight mb-1 text-gray-400">{badge.name}</p>
      <p className="text-[10px] text-gray-600 leading-relaxed mb-1">{badge.description}</p>
      <p className="text-[9px] text-cyan-700 mt-auto">{getConditionHint(badge.condition, badge.conditionValue)}</p>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Badges() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all"); // all | earned | locked

  const { data: allData,  isLoading: loadingAll } = useQuery({
    queryKey: ["gamification-badges"],
    queryFn:  () => api.get("/gamification/badges").then((r) => r.data),
  });

  const { data: mineData, isLoading: loadingMine } = useQuery({
    queryKey: ["gamification-badges-mine"],
    queryFn:  () => api.get("/gamification/badges/mine").then((r) => r.data),
  });

  const toggleShowcase = useMutation({
    mutationFn: (sbId) => api.post(`/gamification/badges/toggle-showcase/${sbId}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["gamification-badges-mine"] }),
  });

  const isLoading = loadingAll || loadingMine;

  if (isLoading) return (
    <div className="flex justify-center pt-20">
      <div className="w-10 h-10 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-transparent border-l-transparent animate-spin"/>
    </div>
  );

  const all      = allData?.badges   || [];
  const mine     = mineData?.badges  || [];
  const earnedMap = new Map(mine.map((sb) => [sb.badgeId, sb]));
  const earnedBadges  = mine;
  const lockedBadges  = all.filter((b) => !earnedMap.has(b.id));
  const pct = all.length > 0 ? Math.round((earnedBadges.length / all.length) * 100) : 0;

  const visibleEarned = filter !== "locked"  ? earnedBadges : [];
  const visibleLocked = filter !== "earned"  ? lockedBadges : [];

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-2 relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.07) 0%, transparent 70%)" }}/>
        <div className="text-5xl mb-3 inline-block animate-float"
          style={{ filter: "drop-shadow(0 0 22px rgba(139,92,246,0.7))" }}>🏷️</div>
        <h1 className="text-2xl font-black">Badges</h1>
        <p className="text-xs text-gray-500 mt-1">{earnedBadges.length} / {all.length} freigeschaltet</p>
      </div>

      {/* ── Progress bar ───────────────────────────────────────────────────── */}
      {all.length > 0 && (
        <div className="p-4 rounded-2xl border border-nexus-border relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(19,19,58,0.9), rgba(13,13,43,0.85))" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold">Gesamtfortschritt</span>
            <span className="text-xs font-black text-violet-400">{pct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(139,92,246,0.12)" }}>
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{
                width: `${pct}%`,
                background: "linear-gradient(90deg, #8b5cf6, #06b6d4)",
                boxShadow: "0 0 8px rgba(139,92,246,0.6)",
              }}
            />
          </div>
          {/* Rarity breakdown */}
          <div className="flex gap-3 mt-2.5">
            {(["common","rare","epic","legendary"]).map((r) => {
              const count = mine.filter((sb) => sb.badge.rarity === r).length;
              if (!count) return null;
              const m = RARITY[r];
              return (
                <div key={r} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: m.color, boxShadow: `0 0 4px ${m.color}` }}/>
                  <span className="text-[10px] text-gray-400">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filter tabs ────────────────────────────────────────────────────── */}
      {all.length > 0 && (
        <div className="flex gap-1 p-1 rounded-xl border border-nexus-border" style={{ background: "rgba(13,13,43,0.6)" }}>
          {[
            { key: "all",    label: "Alle" },
            { key: "earned", label: `Erhalten (${earnedBadges.length})` },
            { key: "locked", label: `Gesperrt (${lockedBadges.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={clsx(
                "flex-1 py-1.5 rounded-lg text-xs font-black transition-all duration-200",
                filter === t.key
                  ? "bg-violet-600 text-white shadow-lg"
                  : "text-gray-400 hover:text-gray-200"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Empty state (no badges created yet) ────────────────────────────── */}
      {all.length === 0 && (
        <div className="p-10 rounded-2xl border border-nexus-border text-center"
          style={{ background: "rgba(13,13,43,0.5)" }}>
          <div className="text-5xl mb-3">🏷️</div>
          <p className="font-black text-lg">Noch keine Badges</p>
          <p className="text-xs text-gray-500 mt-1">Badges werden von Administratoren erstellt.</p>
        </div>
      )}

      {/* ── Earned ─────────────────────────────────────────────────────────── */}
      {visibleEarned.length > 0 && (
        <section>
          <h3 className="font-black text-sm mb-3 flex items-center gap-2">
            <span className="text-green-400">✅</span>
            <span>Freigeschaltet ({earnedBadges.length})</span>
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {visibleEarned.map((sb) => (
              <EarnedBadge key={sb.id} sb={sb} onToggleShowcase={(id) => toggleShowcase.mutate(id)} />
            ))}
          </div>
        </section>
      )}

      {/* ── Locked ─────────────────────────────────────────────────────────── */}
      {visibleLocked.length > 0 && (
        <section>
          <h3 className="font-black text-sm mb-3 flex items-center gap-2">
            <span className="text-gray-500">🔒</span>
            <span className="text-gray-400">Noch zu verdienen ({lockedBadges.length})</span>
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {visibleLocked.map((b) => <LockedBadge key={b.id} badge={b} />)}
          </div>
        </section>
      )}

      {/* All cleared state */}
      {all.length > 0 && lockedBadges.length === 0 && filter !== "earned" && (
        <div className="p-8 rounded-2xl border border-yellow-500/30 text-center"
          style={{ background: "rgba(251,191,36,0.04)" }}>
          <div className="text-5xl mb-3" style={{ filter: "drop-shadow(0 0 16px rgba(251,191,36,0.6))" }}>🎊</div>
          <p className="font-black text-lg" style={{ background: "linear-gradient(90deg,#fbbf24,#f59e0b)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Alle Badges!
          </p>
          <p className="text-xs text-gray-500 mt-1">Du hast jeden Badge freigeschaltet. Absolut legendär.</p>
        </div>
      )}

      <div className="pb-2"/>
    </div>
  );
}
